import { useMemo } from "react";

import { FieldArray, getIn, useField, useFormikContext } from "formik";

import { getSummary } from "components/elements/TiVoTargetsSummary/utils";
import { NumberField, TiVoTargetsField } from "components/form/fields";
import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import { Column, Row, Section, VerticalTabs } from "components/layout";
import { TiVoTargets, ZoomConfiguration } from "hooks/tivoApps";

import { TEXTS } from "../constants";
import { FIELDS_ZOOM_CONFIG, VARIANT_FIELDS } from "../fields";
import { VariantForm } from "../types";
import styles from "./styles.scss";

const getInitialZoomConfig = () => ({
  targets: {},
  zoomFactor: "",
  zoomFactorRatio: "",
});

const getFieldConfig = (
  name: string,
  tabIndex: number,
  fieldKey: keyof typeof FIELDS_ZOOM_CONFIG
) => {
  const { name: suffix, ...field } = FIELDS_ZOOM_CONFIG[fieldKey];
  return { ...field, name: `${name}.${tabIndex}.${suffix}` };
};

export function TiVoAppFormZoomConfigurationTab() {
  const { getDeviceLabel } = useTiVoDeviceData();
  const { errors } = useFormikContext<VariantForm>();
  const [, { value: targetsField }] = useField<TiVoTargets>(
    VARIANT_FIELDS.LAUNCH_CONFIG.TARGETS.name
  );

  const [, { value: zoomConfigs }] = useField<ZoomConfiguration[]>(
    VARIANT_FIELDS.ZOOM_CONFIG.CONFIGURATIONS.name
  );

  const tabNames = useMemo(
    () =>
      zoomConfigs?.length
        ? zoomConfigs.map(({ targets }, index) => {
            return {
              name: getSummary(targets, getDeviceLabel),
              invalid: getIn(
                errors,
                `${VARIANT_FIELDS.ZOOM_CONFIG.CONFIGURATIONS.name}.${index}`
              ),
            };
          })
        : [],
    [zoomConfigs, errors, getDeviceLabel]
  );

  return (
    <>
      <Section header={TEXTS.ZOOM_CONFIG.HEADER}>
        <FieldArray name={VARIANT_FIELDS.ZOOM_CONFIG.CONFIGURATIONS.name}>
          {({ name, push, remove }) => (
            <VerticalTabs
              tabNames={tabNames}
              dataTestId="zoom-configurations-tabs"
              onAdd={() => push(getInitialZoomConfig())}
              onRemove={(tabIndex: number) => remove(tabIndex)}
            >
              {(tabIndex: number) => {
                const tab = zoomConfigs[tabIndex];
                if (!tab) {
                  return null;
                }

                return (
                  <Row>
                    <Column>
                      <NumberField
                        {...getFieldConfig(name, tabIndex, "ZOOM_FACTOR")}
                        step="1"
                      />
                      <NumberField
                        {...getFieldConfig(name, tabIndex, "ZOOM_FACTOR_RATIO")}
                        step="0.1"
                      />
                    </Column>
                    <Column>
                      <div className={styles.targetsField}>
                        <TiVoTargetsField
                          {...getFieldConfig(name, tabIndex, "TARGETS")}
                          restrictTargets={targetsField}
                        />
                      </div>
                    </Column>
                  </Row>
                );
              }}
            </VerticalTabs>
          )}
        </FieldArray>
      </Section>
    </>
  );
}
